import React, { useState } from 'react';
import { Layout } from '../components/Layout.tsx';
import { serviceAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import dayjs from 'dayjs';
import {
  Badge,
  Button,
  Divider,
  Grid,
  Group,
  Modal,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { CloudUpload, Download, Search } from 'lucide-react';
import { PageHeader } from '../components/app/index.ts';
import { toast } from '../lib/toast.ts';
import { recordRef } from '../lib/records.ts';

const CrossMatchDailyActivity = () => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedDescription, setSelectedDescription] = useState<string | null>(
    null
  );
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [counts, setCounts] = useState<{
    odooOnlyCount: number;
    localOnlyCount: number;
    totalCombined: number;
  } | null>(null);
  const { project } = useAuth();

  const handleProcess = async () => {
    try {
      setLoading(true);
      const response = await serviceAPI.crossMatchDailyActivity(
        fromDate,
        toDate,
        project?.odoo_configuration_id || ''
      );
      if (response.success && response.data) {
        setRecords(response.data);
        setCounts({
          odooOnlyCount: response.odooOnlyCount || 0,
          localOnlyCount: response.localOnlyCount || 0,
          totalCombined: response.totalCombined || 0,
        });
      }
    } catch (error) {
      console.error('Error processing cross-match:', error);
      toast.error('Error processing cross-match');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncToLocal = async (record: any) => {
    try {
      setSyncingId(record.id);
      const res = await serviceAPI.syncDailyActivityToLocal(record);
      if (res.success) {
        toast.success('Created local record');
        handleProcess();
      } else {
        toast.error(res.message || 'Failed to create local record');
      }
    } catch (error) {
      console.error('Sync failed:', error);
      toast.error('Failed to create local record');
    } finally {
      setSyncingId(null);
    }
  };

  const handleSyncToOdoo = async (record: any) => {
    try {
      setSyncingId(record.id || record._id);
      const res = await serviceAPI.syncDailyActivityToOdoo(
        record.id,
        project?.odoo_configuration_id || ''
      );
      if (res.success) {
        toast.success('Synced to Odoo');
        handleProcess();
      } else {
        toast.error(res.message || 'Failed to sync to Odoo');
      }
    } catch (error) {
      console.error('Sync failed:', error);
      toast.error('Failed to sync to Odoo');
    } finally {
      setSyncingId(null);
    }
  };

  const filteredRecords = React.useMemo(() => {
    return records.filter((record) => {
      const isLocal = record.matchStatus === 'LOCAL_ONLY';
      const employeeName =
        (isLocal
          ? record.employee?.name || record.resolved_employee_name
          : record.resolved_employee_name) || '';
      const employeeId = String(record.employee_id || '');
      const recordId = String(record.id || record._id || '');
      // What the ID column shows: the job id while a local record is queued.
      const reference = recordRef(record).toLowerCase();

      const query = searchQuery.toLowerCase();
      const matchesSearch =
        recordId.includes(query) ||
        reference.includes(query) ||
        employeeName.toLowerCase().includes(query) ||
        employeeId.includes(query) ||
        record.area_visited?.toLowerCase().includes(query) ||
        record.description?.toLowerCase().includes(query);

      const matchesStatus =
        statusFilter === 'ALL' || record.matchStatus === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [records, searchQuery, statusFilter]);

  const today = new Date().toISOString().split('T')[0];

  return (
    <Layout>
      <PageHeader title="Cross-Match Daily Activity" />

      <Stack gap="lg">
        <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }}>
          <Stack gap="lg">
            <Grid align="flex-end">
              <Grid.Col span={{ base: 12, md: 4 }}>
                <DateInput
                  label="From Date"
                  placeholder="Pick a date"
                  valueFormat="DD-MM-YYYY"
                  maxDate={today}
                  value={fromDate || null}
                  onChange={(value) => setFromDate(value || '')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <DateInput
                  label="To Date"
                  placeholder="Pick a date"
                  valueFormat="DD-MM-YYYY"
                  maxDate={today}
                  value={toDate || null}
                  onChange={(value) => setToDate(value || '')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Button
                  fullWidth
                  onClick={handleProcess}
                  disabled={!fromDate || !toDate || loading}
                >
                  {loading ? 'Processing...' : 'Process Records'}
                </Button>
              </Grid.Col>
            </Grid>

            {records.length > 0 && (
              <>
                <Divider />
                <SimpleGrid cols={{ base: 1, md: 2 }}>
                  <TextInput
                    label="Search"
                    placeholder="Search by ID, employee, area, or description..."
                    leftSection={<Search size={16} />}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.currentTarget.value)}
                  />
                  <Select
                    label="Status Filter"
                    data={[
                      { value: 'ALL', label: 'All Statuses' },
                      { value: 'ODOO_ONLY', label: 'Odoo Only' },
                      { value: 'LOCAL_ONLY', label: 'Local Only' },
                    ]}
                    value={statusFilter}
                    onChange={(value) => setStatusFilter(value || 'ALL')}
                    allowDeselect={false}
                  />
                </SimpleGrid>
              </>
            )}
          </Stack>
        </Paper>

        {counts && (
          <SimpleGrid cols={{ base: 1, md: 3 }}>
            <Paper withBorder shadow="sm" p="md">
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Missing in Local (Odoo Only)
              </Text>
              <Text fz={28} fw={700} c="yellow">
                {counts.odooOnlyCount}
              </Text>
            </Paper>
            <Paper withBorder shadow="sm" p="md">
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Missing in Odoo (Local Only)
              </Text>
              <Text fz={28} fw={700} c="indigo">
                {counts.localOnlyCount}
              </Text>
            </Paper>
            <Paper withBorder shadow="sm" p="md">
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Total Mismatches
              </Text>
              <Text fz={28} fw={700}>
                {counts.totalCombined}
              </Text>
            </Paper>
          </SimpleGrid>
        )}

        <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }}>
          {records.length > 0 ? (
            <Table.ScrollContainer minWidth={1100}>
              <Table withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>ID</Table.Th>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Area Visited</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th>Employee</Table.Th>
                    <Table.Th>KM Traveled</Table.Th>
                    <Table.Th>Closing Meter</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredRecords.map((record, index) => {
                    const isLocal = record.matchStatus === 'LOCAL_ONLY';
                    const isOdoo = record.matchStatus === 'ODOO_ONLY';
                    const employeeName =
                      isLocal && record.employee
                        ? record.employee.name
                        : record.resolved_employee_name || 'Unknown';

                    return (
                      <Table.Tr key={record.id || record._id || index}>
                        <Table.Td fw={500}>
                          {/* A queued local record has no Odoo id yet. */}
                          {isLocal ? recordRef(record) : record.id || '-'}
                        </Table.Td>
                        <Table.Td>
                          {record.date
                            ? dayjs(record.date).format('DD/MM/YYYY')
                            : '-'}
                        </Table.Td>
                        <Table.Td
                          onClick={() => setSelectedDescription(record.area_visited)}
                          title="Click to see full area"
                          style={{ cursor: 'pointer' }}
                        >
                          <Text size="sm" maw={150} truncate>
                            {record.area_visited || '-'}
                          </Text>
                        </Table.Td>
                        <Table.Td
                          onClick={() => setSelectedDescription(record.description)}
                          title="Click to see full description"
                          style={{ cursor: 'pointer' }}
                        >
                          <Text size="sm" maw={150} truncate>
                            {record.description || '-'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{employeeName}</Text>
                          {!isLocal && record.employee_id && (
                            <Text size="xs" c="dimmed">
                              ID: {record.employee_id}
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>{record.km_traveled || 0} km</Table.Td>
                        <Table.Td>{record.closing_meter || 0}</Table.Td>
                        <Table.Td>
                          <Badge
                            variant="light"
                            color={isOdoo ? 'yellow' : 'indigo'}
                          >
                            {String(record.matchStatus || '').replace('_', ' ')}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs" wrap="nowrap">
                            {isLocal && (
                              <Button
                                size="xs"
                                leftSection={<CloudUpload size={14} />}
                                onClick={() => handleSyncToOdoo(record)}
                                loading={
                                  syncingId === (record.id || record._id)
                                }
                                disabled={!!syncingId}
                              >
                                Sync to Odoo
                              </Button>
                            )}
                            {isOdoo && (
                              <Button
                                size="xs"
                                variant="light"
                                leftSection={<Download size={14} />}
                                onClick={() => handleSyncToLocal(record)}
                                loading={syncingId === record.id}
                                disabled={!!syncingId}
                              >
                                Create Locally
                              </Button>
                            )}
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          ) : (
            <Text ta="center" c="dimmed" py="xl">
              {loading
                ? 'Fetching records...'
                : 'Select date range and click "Process Records" to see comparisons.'}
            </Text>
          )}
        </Paper>
      </Stack>

      <Modal
        opened={!!selectedDescription}
        onClose={() => setSelectedDescription(null)}
        title={<Text fw={600}>Details</Text>}
      >
        <Text
          size="sm"
          c="dimmed"
          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          {selectedDescription}
        </Text>
        <Group justify="flex-end" mt="lg">
          <Button onClick={() => setSelectedDescription(null)}>Close</Button>
        </Group>
      </Modal>
    </Layout>
  );
};

export default CrossMatchDailyActivity;
